import { useState, useEffect, useRef } from 'react'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import { useTheme } from '@/contexts/ThemeContext'
import { useOrgStore } from '@/stores/orgStore'
import { useAuthStore } from '@/stores/authStore'
import { useChatStore } from '@/stores/chatStore'
import { ChatDrawer } from '@/components/common/ChatDrawer'
import { accountApi } from '@/api/clients/accountApi'
import { authApi } from '@/api/clients/authApi'
import Sidebar from '@/components/layout/Sidebar'
import { ReadinessBreadcrumb, ReadinessIndexSelector, ReadinessDocumentTitle } from '@/components/readiness/HeaderSlot'
import { ModuleStrip } from '@/components/readiness/ModuleNav'
import { NotificationBell } from '@/components/readiness/Bell'
import { ProfileUrlSync } from '@/lib/readiness/profile'
import { useIsHidden } from '@/stores/profileStore'
import { useSyncActionStore } from '@/stores/syncActionStore'
import { useServiceVersion } from '@/api/clients/versionApi'

const READINESS_CHAT_KEY = 'qtz-chat-readiness-open'

// App shell (Sidebar Navigation Spec §1): the product sidebar on the left
// carries the organisation workspace and the module list; the horizontal top
// bar carries the Quantizant mark and wordmark, the breadcrumb for the current
// page, then the page actions and the bell. The two never both show the logo.

export default function Layout() {
  const [showUserMenu, setShowUserMenu] = useState(false)
  const toggleChat = useChatStore((s) => s.toggle)
  const openChat = useChatStore((s) => s.open)
  const closeChat = useChatStore((s) => s.close)
  const isChatOpen = useChatStore((s) => s.isOpen)
  const { theme, toggleTheme } = useTheme()
  // POC §3.1: the portfolio assistant is removed from the top bar.
  const assistantHidden = useIsHidden('portfolio_assistant')
  // Published by the module strip, which owns the readiness sync.
  const syncNow = useSyncActionStore((s) => s.syncNow)
  const syncing = useSyncActionStore((s) => s.syncing)
  const serviceVersion = useServiceVersion().data?.version ?? null

  const { currentOrgId, orgName } = useOrgStore()
  const {
    userId,
    userEmail,
    role,
    orgPlan,
    orgName: rawOrgName,
    refreshToken,
    setOrgInfo,
    setUserInfo,
    clearSession,
  } = useAuthStore()
  const navigate = useNavigate()

  const handleLogout = async () => {
    setShowUserMenu(false)
    // Best effort: revoke the refresh token server-side, but sign out locally
    // either way so a backend that is down cannot trap the user in the app.
    if (refreshToken) {
      await authApi.logout(refreshToken).catch(() => {})
    }
    clearSession()
    navigate('/login', { replace: true })
  }

  useEffect(() => {
    if (!currentOrgId || rawOrgName !== null) return
    accountApi
      .getOrg(currentOrgId)
      .then((org) => setOrgInfo(org.name, org.plan, org.logo_url ?? null))
      .catch(() => {})
  }, [currentOrgId, rawOrgName, setOrgInfo])

  useEffect(() => {
    if (!currentOrgId || !userId || userEmail !== null) return
    accountApi
      .getUser(currentOrgId, userId)
      .then((u) => setUserInfo(u.email, u.role))
      .catch(() => {})
  }, [currentOrgId, userId, userEmail, setUserInfo])

  const userInitials = userEmail ? userEmail.slice(0, 2).toUpperCase() : '?'
  const userRole = role ?? ''

  // Portfolio Assistant (I4 §9o): closed by default, the user's open / closed
  // choice remembered, and an overlay rather than a column that takes page width.
  const chatReady = useRef(false)
  useEffect(() => {
    let saved: string | null = null
    try {
      saved = localStorage.getItem(READINESS_CHAT_KEY)
    } catch {
      /* storage unavailable: closed */
    }
    if (saved === '1' && !assistantHidden) openChat()
    else closeChat()
    chatReady.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    if (!chatReady.current) return
    try {
      localStorage.setItem(READINESS_CHAT_KEY, isChatOpen ? '1' : '0')
    } catch {
      /* not remembered */
    }
  }, [isChatOpen])

  return (
    <div className="qz-app rd-shell min-h-screen bg-background-light dark:bg-background-dark">
      <Sidebar />

      <div className="qz-main min-h-screen flex flex-col">
        <header className="glass sticky top-0 z-30 px-4 sm:px-6 py-3 dark:border-b dark:border-border-dark">
          {/* Wraps at phone widths so the action cluster drops under the title instead of overflowing. */}
          <div className="flex flex-wrap items-center justify-between gap-y-2">
            <div className="qz-topbar-left">
              <Link to="/" className="qz-brand" aria-label="Quantizant home">
                <img src="/favicon.svg" alt="" className="qz-brand-mark" />
                <h1 className="qz-brand-word">Quantizant</h1>
              </Link>
              <span className="qz-topbar-rule" aria-hidden="true" />
              <ReadinessBreadcrumb />
            </div>

            <div className="flex items-center gap-2">
              <ReadinessIndexSelector />

              {!assistantHidden && (
                <button
                  onClick={toggleChat}
                  className="p-2 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5 transition-colors"
                  aria-label="Open assistant"
                  title="Portfolio Assistant"
                >
                  <span className="material-icons text-xl">forum</span>
                </button>
              )}

              {/* Personal notifications bell (I3 §13). */}
              <NotificationBell />

              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5 transition-colors"
                aria-label="Toggle theme"
              >
                <span className="material-icons text-xl">
                  {theme === 'light' ? 'dark_mode' : 'light_mode'}
                </span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowUserMenu((v) => !v)}
                  className="w-8 h-8 bg-primary rounded-full flex items-center justify-center hover:opacity-90 transition-opacity"
                  aria-label="User menu"
                >
                  {userInitials !== '?' ? (
                    <span className="text-xs font-bold text-white">{userInitials}</span>
                  ) : (
                    <span className="material-icons text-white text-base">person</span>
                  )}
                </button>

                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 top-10 z-50 w-64 bg-surface-light dark:bg-surface-dark rounded-2xl shadow-float dark:shadow-none dark:border dark:border-border-dark p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                          {userInitials !== '?' ? (
                            <span className="text-sm font-bold text-white">{userInitials}</span>
                          ) : (
                            <span className="material-icons text-white">person</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-text-light dark:text-text-dark truncate">
                            {userEmail || 'Unknown user'}
                          </p>
                          {userRole && (
                            <p className="text-xs text-subtle-light dark:text-subtle-dark capitalize">
                              {userRole}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="p-3 rounded-xl bg-container-light dark:bg-container-dark">
                        <p className="label-sm text-subtle-light dark:text-subtle-dark mb-1">
                          Organisation
                        </p>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-text-light dark:text-text-dark truncate">
                            {orgName}
                          </p>
                          {orgPlan && (
                            <span className="shrink-0 text-[10px] label-sm px-1.5 py-0.5 rounded-md bg-primary/10 text-primary">
                              {orgPlan}
                            </span>
                          )}
                        </div>
                        <p
                          className="text-xs text-subtle-light dark:text-subtle-dark font-mono mt-1 truncate"
                          title={currentOrgId}
                        >
                          {currentOrgId}
                        </p>
                      </div>

                      {syncNow && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowUserMenu(false)
                            void syncNow()
                          }}
                          disabled={syncing}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-text-light dark:text-text-dark hover:bg-container-light dark:hover:bg-container-dark rounded-xl transition-colors disabled:opacity-60"
                          data-testid="user-menu-sync"
                        >
                          <span className={`material-icons text-base ${syncing ? 'animate-spin' : ''}`}>sync</span>
                          {syncing ? 'Syncing…' : 'Sync now'}
                        </button>
                      )}

                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-error hover:bg-error/5 rounded-xl transition-colors"
                      >
                        <span className="material-icons text-base">logout</span>
                        Sign out
                      </button>

                      <p
                        className="px-3 text-xs font-mono text-subtle-light dark:text-subtle-dark"
                        title={`Portal v${__APP_VERSION__} · Service ${serviceVersion ? `v${serviceVersion}` : 'not reachable'}`}
                        data-testid="user-menu-version"
                      >
                        v{__APP_VERSION__} / {serviceVersion ? `v${serviceVersion}` : '—'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>
        <ReadinessDocumentTitle />
        <ProfileUrlSync />
        <ModuleStrip />

        <main className="flex-grow p-6">
          <Outlet />
        </main>
      </div>

      {!assistantHidden && <ChatDrawer />}
    </div>
  )
}
